import { BrandLockup } from "@/components/BrandLockup";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import {
  FiBookOpen,
  FiChevronDown,
  FiCheckSquare,
  FiGrid,
  FiPieChart,
  FiTag,
  FiTarget,
  FiUser,
} from "react-icons/fi";
import { UserMenu } from "./UserMenu.tsx";
import { useAuth } from "@/hooks/useAuth.ts";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { usePreferencesStore } from "@/stores/usePreferencesStore";

type MenuGroup = "planning" | "more";
type MenuDestination = {
  label: string;
  description: string;
  path: "/budget" | "/installments" | "/savings" | "/saving-pockets" | "/categories" | "/tags" | "/wrapped" | "/profile";
  icon: React.ComponentType<{ className?: string }>;
  color: string;
};

export const NavBar = () => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "nav"]);
  const isAdvancedMode = usePreferencesStore((state) => state.appMode === "advanced");
  const [openMenu, setOpenMenu] = useState<MenuGroup | null>(null);
  const menuRootRef = useRef<HTMLDivElement | null>(null);
  const navTrackRef = useRef<HTMLElement | null>(null);
  const activeNavRef = useRef<HTMLButtonElement | null>(null);
  const [activePill, setActivePill] = useState({ left: 0, width: 0, ready: false, animate: false });
  const triggerRefs = useRef<Record<MenuGroup, HTMLButtonElement | null>>({ planning: null, more: null });
  const menuItemsRef = useRef<Array<HTMLButtonElement | null>>([]);

  const isDashboard = location.pathname.startsWith("/dashboard");
  const isWrapped = location.pathname.startsWith("/wrapped");
  const isAccounts = location.pathname.startsWith("/accounts");
  const isTransactions = location.pathname.startsWith("/transactions");
  const isCategories = location.pathname.startsWith("/categories");
  const isTags = location.pathname.startsWith("/tags");
  const isSavings = location.pathname.startsWith("/savings") && !location.pathname.startsWith("/saving-pockets");
  const isSavingPockets = location.pathname.startsWith("/saving-pockets");
  const isBudget = location.pathname.startsWith("/budget");
  const isInstallments = location.pathname.startsWith("/installments");
  const isAnalytics = location.pathname.startsWith("/analytics");
  const isProfile = location.pathname.startsWith("/profile");

  const planningItems: MenuDestination[] = isAdvancedMode
    ? [
        {
          label: t("nav.budget"),
          description: t("nav.budgetSubtitle"),
          path: "/budget",
          icon: FiPieChart,
          color: "var(--ds-card-mist)",
        },
        {
          label: t("nav.installments"),
          description: t("nav.installmentsSubtitle"),
          path: "/installments",
          icon: FiCheckSquare,
          color: "var(--ds-card-rose)",
        },
        {
          label: t("nav.savings"),
          description: t("nav.savingsSubtitle"),
          path: "/savings",
          icon: FiTarget,
          color: "var(--ds-card-lilac)",
        },
        ...(FEATURE_FLAGS.savingPockets
          ? [{
              label: t("nav.savingPockets"),
              description: t("nav.savingPocketsSubtitle"),
              path: "/saving-pockets" as const,
              icon: FiTarget,
              color: "var(--ds-card-sun)",
            }]
          : []),
      ]
    : [];
  const moreItems: MenuDestination[] = [
    {
      label: t("nav.categories"),
      description: t("nav.categoriesSubtitle"),
      path: "/categories",
      icon: FiGrid,
      color: "var(--ds-card-mist)",
    },
    {
      label: t("nav.tags"),
      description: t("nav.tagsSubtitle"),
      path: "/tags",
      icon: FiTag,
      color: "var(--ds-card-rose)",
    },
    ...(FEATURE_FLAGS.wrapped
      ? [{
          label: t("nav.wrapped"),
          description: t("nav.wrappedSubtitle"),
          path: "/wrapped" as const,
          icon: FiBookOpen,
          color: "var(--ds-card-sun)",
        }]
      : []),
    {
      label: t("common.actions.profile", { defaultValue: "Profile" }),
      description: t("nav.profileSubtitle", { defaultValue: "Personal details and preferences" }),
      path: "/profile",
      icon: FiUser,
      color: "var(--ds-card-lilac)",
    },
  ];

  const activeGroup: MenuGroup | null =
    isBudget || isInstallments || isSavings || isSavingPockets
      ? "planning"
      : isCategories || isTags || isWrapped || isProfile
        ? "more"
        : null;

  const activeNavKey = isDashboard
    ? "home"
    : isTransactions
      ? "transactions"
      : isAccounts
        ? "accounts"
        : isBudget || isInstallments || isSavings || isSavingPockets
          ? "planning"
          : isAnalytics
            ? "analytics"
            : activeGroup === "more"
              ? "more"
              : null;

  useLayoutEffect(() => {
    const track = navTrackRef.current;
    const active = activeNavRef.current;
    if (!track || !active) {
      setActivePill((current) => ({ ...current, ready: false }));
      return undefined;
    }
    // Only the route change animates. Layout shifts (scrollbar appearing with
    // the new page, resize, font load) snap the pill, otherwise it glides twice.
    const update = (animate: boolean) => {
      const trackRect = track.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      setActivePill((current) => ({
        left: activeRect.left - trackRect.left,
        width: activeRect.width,
        ready: true,
        animate: animate && current.ready,
      }));
    };
    update(true);
    const snap = () => update(false);
    // ResizeObserver fires once on observe(); skip it so it doesn't cancel the glide.
    let observed = false;
    const observer = new ResizeObserver(() => {
      if (observed) snap();
      observed = true;
    });
    observer.observe(track);
    observer.observe(active);
    window.addEventListener("resize", snap);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", snap);
    };
  }, [activeNavKey, isAdvancedMode, t]);

  useEffect(() => {
    setOpenMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!openMenu) return undefined;
    const handlePointerDown = (event: PointerEvent) => {
      if (!menuRootRef.current?.contains(event.target as Node)) setOpenMenu(null);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const focused = document.activeElement;
        const trigger = triggerRefs.current[openMenu];
        const menu = menuRootRef.current?.querySelector(`#app-nav-menu-${openMenu}`);
        if (!trigger?.contains(focused) && !menu?.contains(focused)) return;
        const closingMenu = openMenu;
        setOpenMenu(null);
        triggerRefs.current[closingMenu]?.focus();
        return;
      }
      const focused = document.activeElement;
      const trigger = triggerRefs.current[openMenu];
      const menu = menuRootRef.current?.querySelector(`#app-nav-menu-${openMenu}`);
      if (!trigger?.contains(focused) && !menu?.contains(focused)) return;
      const items = menuItemsRef.current.filter((item): item is HTMLButtonElement => Boolean(item));
      if (items.length === 0) return;
      if (event.key === "Home" || event.key === "End") {
        event.preventDefault();
        items[event.key === "Home" ? 0 : items.length - 1]?.focus();
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const current = items.indexOf(document.activeElement as HTMLButtonElement);
      const next = current < 0
        ? (event.key === "ArrowDown" ? 0 : items.length - 1)
        : (current + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
      event.preventDefault();
      items[next]?.focus();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenu]);

  const firstName = (() => {
    if (!user || typeof user !== "object") return undefined;
    const record = user as Record<string, unknown>;
    const value =
      record["firstname"] ??
      record["first_name"] ??
      record["firstName"] ??
      record["given_name"] ??
      record["givenName"] ??
      record["name"] ??
      record["first"];
    return typeof value === "string" ? value : undefined;
  })();
  const lastName = (() => {
    if (!user || typeof user !== "object") return undefined;
    const record = user as Record<string, unknown>;
    const value =
      record["lastname"] ??
      record["last_name"] ??
      record["lastName"] ??
      record["family_name"] ??
      record["familyName"] ??
      record["surname"] ??
      record["last"];
    return typeof value === "string" ? value : undefined;
  })();
  const userInitials = (() => {
    if (firstName && lastName) return ((firstName.trim()[0] ?? "") + (lastName.trim()[0] ?? "")).toUpperCase();
    const first = firstName?.trim();
    if (first) return (first.slice(0, 2) || first[0] || "U").toUpperCase();
    if (user && typeof user === "object") {
      const record = user as Record<string, unknown>;
      const candidate = record["email"] ?? record["username"] ?? record["user_name"] ?? record["login"];
      if (typeof candidate === "string") return (candidate.split("@")[0].slice(0, 2) || "U").toUpperCase();
    }
    return "U";
  })();

  const openGroup = (group: MenuGroup) => {
    setOpenMenu((current) => {
      const next = current === group ? null : group;
      if (next) requestAnimationFrame(() => menuItemsRef.current[0]?.focus());
      return next;
    });
  };

  const handleNavigate = (path: MenuDestination["path"]) => {
    triggerRefs.current[openMenu ?? "more"]?.focus();
    void navigate({ to: path, replace: false });
    setOpenMenu(null);
  };

  const renderMenu = (group: MenuGroup, items: MenuDestination[]) => {
    if (openMenu !== group) return null;
    return (
      <div
        id={group === "planning" ? "app-nav-menu-planning" : "app-nav-menu-more"}
        className="app-nav-menu absolute right-0 top-[calc(100%+var(--app-space-2))] z-[60] w-[min(380px,calc(100vw-2rem))] rounded-[var(--ds-radius-lg)] border-[1.5px] border-[var(--ds-line)] bg-[var(--ds-surface-raised)] p-2"
        role="menu"
        aria-label={group === "planning" ? t("nav.planning") : t("nav.more")}
      >
        {items.map((item, index) => {
          const Icon = item.icon;
          const active = location.pathname.startsWith(item.path);
          return (
            <button
              key={item.path}
              ref={(element) => { menuItemsRef.current[index] = element; }}
              type="button"
              role="menuitem"
              aria-current={active ? "page" : undefined}
              onClick={() => handleNavigate(item.path)}
              className={[
                "app-focus-ring flex min-h-16 w-full items-center gap-4 rounded-[var(--ds-radius-md)] px-3 py-2 text-left transition-colors",
                active ? "bg-[var(--ds-ground)]" : "hover:bg-[var(--ds-ground)]",
              ].join(" ")}
            >
              <span
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[var(--ds-on-card)]"
                style={{ backgroundColor: item.color }}
                aria-hidden="true"
              >
                <Icon className="h-5 w-5" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-base font-medium leading-[22px] text-[var(--ds-ink)]">{item.label}</span>
                <span className="block truncate text-[13px] leading-[18px] text-[var(--ds-ink-muted)]">{item.description}</span>
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const navButtonClass = (active: boolean) => [
    "app-focus-ring relative z-10 inline-flex min-h-11 shrink-0 items-center justify-center gap-1 lg:gap-2 rounded-full px-3 text-sm lg:px-5 lg:text-[15px] transition-colors duration-200",
    active
      ? "font-medium text-[var(--ds-on-card)]"
      : "text-[var(--ds-ink)] hover:bg-[var(--ds-surface-raised)]",
  ].join(" ");

  return (
    <div className={`app-nav bg-[var(--ds-ground)] shadow-none${isTransactions ? " app-nav-transactions" : ""}`} ref={menuRootRef}>
      <div className="mx-auto flex h-[72px] max-w-[92rem] items-center justify-between gap-3 px-4 md:hidden">
        <button type="button" onClick={() => void navigate({ to: "/dashboard", replace: false })} className="app-nav-logo min-w-0" aria-label={t("nav.home")}>
          <BrandLockup />
        </button>
        <UserMenu userInitials={userInitials} avatarSeed={user?.id || user?.email} onProfile={() => void navigate({ to: "/profile", replace: false })} onLogout={logout} />
      </div>

      <div className="mx-auto hidden h-[88px] max-w-[92rem] items-center gap-4 px-5 md:flex lg:gap-10 lg:px-10">
        <button type="button" onClick={() => void navigate({ to: "/dashboard", replace: false })} className="app-nav-logo shrink-0" aria-label={t("nav.home")}>
          <BrandLockup compactAtTablet />
        </button>

        <nav ref={navTrackRef} className="relative flex min-w-0 flex-1 items-center justify-center gap-1" aria-label={t("common:navigation.primaryNavigation")}>
          <span
            className="app-nav-active-indicator pointer-events-none absolute left-0 top-1/2 z-0 h-11 rounded-full bg-[var(--ds-card-sun)]"
            aria-hidden="true"
            style={{
              width: activePill.width,
              transform: `translate(${activePill.left}px, -50%)`,
              opacity: activePill.ready ? 1 : 0,
              transitionProperty: activePill.animate ? undefined : "opacity",
            }}
          />
          <button ref={(element) => { if (activeNavKey === "home") activeNavRef.current = element; }} type="button" onClick={() => void navigate({ to: "/dashboard", replace: false })} aria-current={isDashboard ? "page" : undefined} className={navButtonClass(isDashboard)}>
            {t("nav.home")}
          </button>
          <button ref={(element) => { if (activeNavKey === "transactions") activeNavRef.current = element; }} type="button" onClick={() => void navigate({ to: "/transactions", replace: false })} aria-current={isTransactions ? "page" : undefined} className={navButtonClass(isTransactions)}>
            {t("nav.transactions")}
          </button>
          <button ref={(element) => { if (activeNavKey === "accounts") activeNavRef.current = element; }} type="button" onClick={() => void navigate({ to: "/accounts", replace: false })} aria-current={isAccounts ? "page" : undefined} className={navButtonClass(isAccounts)}>
            {t("nav.accounts")}
          </button>

          {isAdvancedMode ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => openGroup("planning")}
                aria-haspopup="menu"
                aria-expanded={openMenu === "planning"}
                aria-controls={openMenu === "planning" ? "app-nav-menu-planning" : undefined}
                aria-current={activeGroup === "planning" ? "page" : undefined}
                ref={(element) => { triggerRefs.current.planning = element; if (activeNavKey === "planning") activeNavRef.current = element; }}
                className={navButtonClass(openMenu === "planning" || activeGroup === "planning")}
              >
                {t("nav.planning")} <FiChevronDown className="h-4 w-4" aria-hidden="true" />
              </button>
              {renderMenu("planning", planningItems)}
            </div>
          ) : null}

          {isAdvancedMode ? (
            <button ref={(element) => { if (activeNavKey === "analytics") activeNavRef.current = element; }} type="button" onClick={() => void navigate({ to: "/analytics", replace: false })} aria-current={isAnalytics ? "page" : undefined} className={navButtonClass(isAnalytics)}>
              {t("nav.analytics")}
            </button>
          ) : null}

          <div className="relative">
            <button
              type="button"
              onClick={() => openGroup("more")}
              aria-haspopup="menu"
              aria-expanded={openMenu === "more"}
              aria-controls={openMenu === "more" ? "app-nav-menu-more" : undefined}
              aria-current={activeGroup === "more" ? "page" : undefined}
              ref={(element) => { triggerRefs.current.more = element; if (activeNavKey === "more") activeNavRef.current = element; }}
              className={navButtonClass(openMenu === "more" || activeGroup === "more")}
            >
              {t("nav.more")} <FiChevronDown className="h-4 w-4" aria-hidden="true" />
            </button>
            {renderMenu("more", moreItems)}
          </div>
        </nav>

        <UserMenu userInitials={userInitials} avatarSeed={user?.id || user?.email} onProfile={() => void navigate({ to: "/profile", replace: false })} onLogout={logout} />
      </div>
    </div>
  );
};
